import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Download, FileText, Users, ShieldCheck, AlertTriangle, Clock, BookOpen, Bookmark } from 'lucide-react';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';
import DataTable, { Column } from '@/components/ui/DataTable';
import PillBadge from '@/components/ui/PillBadge';
import Modal from '@/components/ui/Modal';
import SideDrawer from '@/components/ui/SideDrawer';
import { useTableExport } from '@/hooks/useTableExport';
import { useSavedFilters } from '@/hooks/useSavedFilters';
import { format, differenceInDays } from 'date-fns';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TrainingRecord {
  id: string;
  staffName: string;
  employeeId: string;
  lab: string;
  role: string;
  trainingModule: string;
  type: string;
  status: string;
  completionDate?: string;
  expiryDate?: string;
  daysUntilExpiry?: number;
  trainer?: string;
  assessmentScore?: number;
  allRecords?: TrainingHistoryItem[];
}

interface TrainingHistoryItem {
  id: string;
  module: string;
  type: string;
  completionDate: string;
  expiryDate?: string;
  score?: number;
  trainer: string;
  status: string;
}

// ─── Mock Data ─────────────────────────────────────────────────────────────────

const MOCK_TRAINING: TrainingRecord[] = [
  {
    id: '1', staffName: 'Priya Sharma', employeeId: 'EMP-1042', lab: 'QC Lab 1', role: 'Senior Analyst', trainingModule: 'GMP Fundamentals – Annex 11', type: 'gmp', status: 'current', completionDate: '2023-09-01', expiryDate: '2025-09-01', daysUntilExpiry: 150, trainer: 'Dr. Anita Rao',
    allRecords: [
      { id: 'h1', module: 'GMP Fundamentals – Annex 11', type: 'gmp', completionDate: '2023-09-01', expiryDate: '2025-09-01', score: 92, trainer: 'Dr. Anita Rao', status: 'current' },
      { id: 'h2', module: 'HPLC Operation & Maintenance', type: 'equipment', completionDate: '2024-01-15', expiryDate: '2026-01-15', score: 88, trainer: 'Suresh Nair', status: 'current' },
      { id: 'h3', module: 'Chemical Safety & Handling', type: 'safety', completionDate: '2023-06-10', expiryDate: '2024-06-10', score: 95, trainer: 'Safety Officer', status: 'expired' },
    ],
  },
  {
    id: '2', staffName: 'Anil Verma', employeeId: 'EMP-1055', lab: 'Microbiology Lab', role: 'Microbiologist', trainingModule: 'Aseptic Technique & Sterility Testing', type: 'sop', status: 'expiring_soon', completionDate: '2023-04-01', expiryDate: '2024-04-01', daysUntilExpiry: 22, trainer: 'Dr. Meera Singh',
    allRecords: [
      { id: 'h4', module: 'Aseptic Technique & Sterility Testing', type: 'sop', completionDate: '2023-04-01', expiryDate: '2024-04-01', score: 85, trainer: 'Dr. Meera Singh', status: 'expiring_soon' },
    ],
  },
  {
    id: '3', staffName: 'Kavya Menon', employeeId: 'EMP-1061', lab: 'Analytical Lab', role: 'QC Analyst', trainingModule: 'Regulatory Affairs – ICH Guidelines', type: 'regulatory', status: 'expired', completionDate: '2022-11-15', expiryDate: '2023-11-15', daysUntilExpiry: -120, trainer: 'Compliance Head',
    allRecords: [
      { id: 'h5', module: 'Regulatory Affairs – ICH Guidelines', type: 'regulatory', completionDate: '2022-11-15', expiryDate: '2023-11-15', score: 78, trainer: 'Compliance Head', status: 'expired' },
    ],
  },
  {
    id: '4', staffName: 'Deepak Rao', employeeId: 'EMP-1033', lab: 'QC Lab 2', role: 'Lab Technician', trainingModule: 'Fire Safety & Emergency Procedures', type: 'safety', status: 'not_started', trainer: '', daysUntilExpiry: undefined,
    allRecords: [],
  },
  {
    id: '5', staffName: 'Meena Pillai', employeeId: 'EMP-1077', lab: 'Microbiology Lab', role: 'Senior Microbiologist', trainingModule: 'Environmental Monitoring SOP v4', type: 'sop', status: 'current', completionDate: '2024-01-20', expiryDate: '2026-01-20', daysUntilExpiry: 660, trainer: 'QA Head',
    allRecords: [
      { id: 'h6', module: 'Environmental Monitoring SOP v4', type: 'sop', completionDate: '2024-01-20', expiryDate: '2026-01-20', score: 96, trainer: 'QA Head', status: 'current' },
      { id: 'h7', module: 'GMP Refresher 2024', type: 'gmp', completionDate: '2024-02-05', score: 90, trainer: 'Dr. Anita Rao', status: 'current' },
    ],
  },
  {
    id: '6', staffName: 'Farhan Khan', employeeId: 'EMP-1089', lab: 'Synthesis Lab A', role: 'Process Chemist', trainingModule: 'Weighing Balance Calibration – SOP WB-003', type: 'equipment', status: 'expiring_soon', completionDate: '2023-03-10', expiryDate: '2024-03-10', daysUntilExpiry: 15, trainer: 'Instruments Team',
    allRecords: [
      { id: 'h8', module: 'Weighing Balance Calibration – SOP WB-003', type: 'equipment', completionDate: '2023-03-10', expiryDate: '2024-03-10', score: 82, trainer: 'Instruments Team', status: 'expiring_soon' },
    ],
  },
];

const STATUS_OPTIONS = ['current', 'expiring_soon', 'expired', 'not_started'];
const TYPE_OPTIONS = ['gmp', 'safety', 'equipment', 'sop', 'regulatory'];
const LABS = ['QC Lab 1', 'QC Lab 2', 'Microbiology Lab', 'Analytical Lab', 'Synthesis Lab A', 'Synthesis Lab B'];
const MODULES = [
  'GMP Fundamentals – Annex 11',
  'Aseptic Technique & Sterility Testing',
  'HPLC Operation & Maintenance',
  'Chemical Safety & Handling',
  'Fire Safety & Emergency Procedures',
  'Environmental Monitoring SOP v4',
  'ICH Guidelines – Regulatory Overview',
  'Weighing Balance Calibration – SOP WB-003',
  'GMP Refresher (Annual)',
  'Data Integrity & 21 CFR Part 11',
];

// ─── Stat Card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, color }: { label: string; value: number; icon: React.ElementType; color: string }) {
  return (
    <div className="card p-4 flex items-center gap-4">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-bold font-serif text-text">{value}</p>
        <p className="text-xs text-text-muted">{label}</p>
      </div>
    </div>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function Training() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [labFilter, setLabFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showAssign, setShowAssign] = useState(false);
  const [selected, setSelected] = useState<TrainingRecord | null>(null);
  const [showSaveFilter, setShowSaveFilter] = useState(false);
  const [saveFilterName, setSaveFilterName] = useState('');
  const [form, setForm] = useState({
    staffSearch: '',
    trainingModule: '',
    type: 'gmp',
    dueDate: '',
    trainer: '',
    notes: '',
  });
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['training', { search, status: statusFilter, lab: labFilter, type: typeFilter, page }],
    queryFn: () =>
      api.get('/training', { params: { search, status: statusFilter, lab: labFilter, type: typeFilter, page, limit: 20 } })
        .then((r) => r.data)
        .catch(() => ({ data: MOCK_TRAINING, meta: { total: MOCK_TRAINING.length } })),
  });

  const rows: TrainingRecord[] = data?.data ?? MOCK_TRAINING;
  const total: number = data?.meta?.total ?? MOCK_TRAINING.length;

  const filtered = rows.filter((t) => {
    const matchSearch = !search || t.staffName.toLowerCase().includes(search.toLowerCase()) || t.employeeId.toLowerCase().includes(search.toLowerCase()) || t.trainingModule.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || t.status === statusFilter;
    const matchLab = !labFilter || t.lab === labFilter;
    const matchType = !typeFilter || t.type === typeFilter;
    return matchSearch && matchStatus && matchLab && matchType;
  });

  const assignMutation = useMutation({
    mutationFn: (payload: typeof form) => api.post('/training/assign', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training'] });
      setShowAssign(false);
      setForm({ staffSearch: '', trainingModule: '', type: 'gmp', dueDate: '', trainer: '', notes: '' });
    },
  });

  const exportColumns = [
    { key: 'staffName', label: 'Staff Name' },
    { key: 'employeeId', label: 'Employee ID' },
    { key: 'lab', label: 'Lab' },
    { key: 'role', label: 'Role' },
    { key: 'trainingModule', label: 'Training Module' },
    { key: 'type', label: 'Type' },
    { key: 'status', label: 'Status' },
    { key: 'completionDate', label: 'Completion Date' },
    { key: 'expiryDate', label: 'Expiry Date' },
  ];
  const { exportCSV, exportPDF } = useTableExport(filtered as unknown as Record<string, unknown>[], exportColumns, 'training-records');

  const { saved: savedFilters, saveFilter, loadFilter, deleteFilter } = useSavedFilters('training');

  const staffSet = new Set(filtered.map((t) => t.employeeId));
  const stats = {
    totalStaff: staffSet.size,
    compliant: filtered.filter((t) => t.status === 'current').length,
    nonCompliant: filtered.filter((t) => t.status === 'expired' || t.status === 'not_started').length,
    overdue: filtered.filter((t) => t.status === 'expired').length,
    expiringSoon: filtered.filter((t) => t.status === 'expiring_soon').length,
  };

  const columns: Column<Record<string, unknown>>[] = [
    {
      key: 'staffName', label: 'Staff',
      render: (row) => (
        <div>
          <p className="font-medium text-sm text-text">{String(row.staffName)}</p>
          <p className="text-xs text-text-dim">{String(row.employeeId)} · {String(row.role)}</p>
        </div>
      ),
    },
    {
      key: 'lab', label: 'Lab', width: 'w-32',
      render: (row) => <span className="text-xs text-text-muted">{String(row.lab)}</span>,
    },
    {
      key: 'trainingModule', label: 'Training Module',
      render: (row) => (
        <div>
          <p className="text-xs font-medium text-text truncate max-w-xs">{String(row.trainingModule)}</p>
          <p className="text-[10px] text-text-dim uppercase font-medium mt-0.5">{String(row.type)}</p>
        </div>
      ),
    },
    {
      key: 'status', label: 'Status', width: 'w-36',
      render: (row) => {
        const st = String(row.status);
        return (
          <div className="flex items-center gap-1.5">
            {st === 'expired' && <span className="w-1 h-8 rounded-full flex-shrink-0" style={{ background: '#DC2626' }} />}
            {st === 'expiring_soon' && <span className="w-1 h-8 rounded-full flex-shrink-0" style={{ background: '#D97706' }} />}
            <PillBadge value={st} showDot />
          </div>
        );
      },
    },
    {
      key: 'completionDate', label: 'Completed', width: 'w-28',
      render: (row) => (
        <span className="text-xs font-mono text-text-muted">
          {row.completionDate ? format(new Date(String(row.completionDate)), 'd MMM yyyy') : '—'}
        </span>
      ),
    },
    {
      key: 'expiryDate', label: 'Expires', width: 'w-28',
      render: (row) => {
        const expiry = row.expiryDate ? String(row.expiryDate) : null;
        const days = row.daysUntilExpiry !== undefined ? Number(row.daysUntilExpiry) : null;
        if (!expiry) return <span className="text-xs text-text-dim">—</span>;
        return (
          <div>
            <p className="text-xs font-mono text-text-muted">{format(new Date(expiry), 'd MMM yyyy')}</p>
            {days !== null && days <= 30 && days > 0 && (
              <p className="text-[10px] font-semibold" style={{ color: '#D97706' }}>{days}d left</p>
            )}
            {days !== null && days < 0 && (
              <p className="text-[10px] font-semibold" style={{ color: '#DC2626' }}>Expired {Math.abs(days)}d ago</p>
            )}
          </div>
        );
      },
    },
  ];

  const handleApplyFilter = (id: string) => {
    const f = loadFilter(id);
    if (!f) return;
    if (f.status !== undefined) setStatusFilter(f.status);
    if (f.lab !== undefined) setLabFilter(f.lab);
    if (f.type !== undefined) setTypeFilter(f.type);
    if (f.search !== undefined) setSearch(f.search);
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Training & Competency"
        subtitle="Staff certifications · Competency assessments · Expiry tracking"
        actions={
          <>
            <button onClick={exportPDF} className="btn-ghost"><FileText size={13} /> PDF</button>
            <button onClick={exportCSV} className="btn-ghost"><Download size={13} /> CSV</button>
            <button onClick={() => setShowAssign(true)} className="btn-primary"><Plus size={13} /> Assign Training</button>
          </>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard label="Total Staff" value={stats.totalStaff} icon={Users} color="#b07d1a" />
        <StatCard label="Compliant" value={stats.compliant} icon={ShieldCheck} color="#16A34A" />
        <StatCard label="Non-Compliant" value={stats.nonCompliant} icon={AlertTriangle} color="#DC2626" />
        <StatCard label="Overdue Training" value={stats.overdue} icon={Clock} color="#DC2626" />
        <StatCard label="Expiring in 30 Days" value={stats.expiringSoon} icon={BookOpen} color="#D97706" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
          <input
            placeholder="Search by staff name, ID, or training module..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="input pl-8"
          />
        </div>

        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="select w-36">
          <option value="">All Status</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>

        <select value={labFilter} onChange={(e) => { setLabFilter(e.target.value); setPage(1); }} className="select w-40">
          <option value="">All Labs</option>
          {LABS.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>

        <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }} className="select w-32">
          <option value="">All Types</option>
          {TYPE_OPTIONS.map((t) => <option key={t} value={t} className="uppercase">{t.toUpperCase()}</option>)}
        </select>

        {/* Saved Filters */}
        <div className="relative">
          <button onClick={() => setShowSaveFilter(!showSaveFilter)} className="btn-ghost">
            <Bookmark size={13} /> Saved Filters
            {savedFilters.length > 0 && (
              <span className="ml-1 w-4 h-4 rounded-full bg-teal text-white text-[9px] flex items-center justify-center">{savedFilters.length}</span>
            )}
          </button>
          {showSaveFilter && (
            <div className="absolute right-0 top-10 z-30 card p-3 w-60 space-y-2 shadow-xl">
              <p className="text-xs font-semibold text-text-muted">Saved Filters</p>
              {savedFilters.length === 0 && <p className="text-xs text-text-dim">No saved filters yet.</p>}
              {savedFilters.map((f) => (
                <div key={f.id} className="flex items-center justify-between group">
                  <button onClick={() => { handleApplyFilter(f.id); setShowSaveFilter(false); }} className="text-xs text-text hover:text-teal">{f.name}</button>
                  <button onClick={() => deleteFilter(f.id)} className="text-[10px] text-red-500 opacity-0 group-hover:opacity-100">remove</button>
                </div>
              ))}
              <div className="border-t border-border pt-2 flex gap-1">
                <input className="input text-xs flex-1 py-1" placeholder="Filter name..." value={saveFilterName} onChange={(e) => setSaveFilterName(e.target.value)} />
                <button
                  onClick={() => {
                    if (saveFilterName) {
                      saveFilter(saveFilterName, { status: statusFilter, lab: labFilter, type: typeFilter, search });
                      setSaveFilterName('');
                    }
                  }}
                  className="btn-primary text-xs py-1 px-2"
                >
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filtered as unknown as Record<string, unknown>[]}
        loading={isLoading}
        onRowClick={(row) => setSelected(row as unknown as TrainingRecord)}
        total={total}
        page={page}
        onPageChange={setPage}
      />

      {/* Assign Training Modal */}
      <Modal
        isOpen={showAssign}
        onClose={() => setShowAssign(false)}
        title="Assign Training"
        subtitle="Assign a training module to a staff member"
        size="lg"
        footer={
          <>
            <button onClick={() => setShowAssign(false)} className="btn-ghost">Cancel</button>
            <button onClick={() => assignMutation.mutate(form)} disabled={assignMutation.isPending} className="btn-primary">
              {assignMutation.isPending ? 'Assigning...' : 'Assign Training'}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Staff Member *</label>
            <input className="input" value={form.staffSearch} onChange={(e) => setForm((f) => ({ ...f, staffSearch: e.target.value }))} placeholder="Employee name or ID..." />
          </div>
          <div className="col-span-2">
            <label className="label">Training Module *</label>
            <select className="select" value={form.trainingModule} onChange={(e) => setForm((f) => ({ ...f, trainingModule: e.target.value }))}>
              <option value="">Select module...</option>
              {MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Training Type *</label>
            <select className="select" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
              {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Due Date *</label>
            <input type="date" className="input" value={form.dueDate} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} />
          </div>
          <div>
            <label className="label">Trainer / Instructor</label>
            <input className="input" value={form.trainer} onChange={(e) => setForm((f) => ({ ...f, trainer: e.target.value }))} placeholder="Trainer name..." />
          </div>
          <div>
            <label className="label">Notes</label>
            <input className="input" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Special instructions..." />
          </div>
        </div>
      </Modal>

      {/* Staff Training Detail SideDrawer */}
      <SideDrawer
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.staffName || ''}
        subtitle={`${selected?.employeeId} · ${selected?.lab} · ${selected?.role}`}
        width={520}
      >
        {selected && (
          <div className="space-y-5">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-bg rounded-lg">
                <p className="text-[10px] uppercase font-semibold text-text-dim mb-1">Current Status</p>
                <PillBadge value={selected.status} showDot />
              </div>
              <div className="p-3 bg-bg rounded-lg">
                <p className="text-[10px] uppercase font-semibold text-text-dim mb-1">Lab</p>
                <p className="text-xs font-medium text-text">{selected.lab}</p>
              </div>
              <div className="p-3 bg-bg rounded-lg">
                <p className="text-[10px] uppercase font-semibold text-text-dim mb-1">Role</p>
                <p className="text-xs font-medium text-text">{selected.role}</p>
              </div>
            </div>

            {/* Training records */}
            <div>
              <p className="text-xs font-semibold text-text-muted mb-3 flex items-center gap-1.5">
                <BookOpen size={12} className="text-teal" /> All Training Records
              </p>
              {(selected.allRecords || []).length === 0 ? (
                <p className="text-xs text-text-dim">No training records on file.</p>
              ) : (
                <div className="space-y-2">
                  {(selected.allRecords || []).map((rec) => (
                    <div key={rec.id} className="p-3 rounded-lg border border-border" style={rec.status === 'expired' ? { borderColor: 'rgba(220,38,38,0.3)', background: 'rgba(220,38,38,0.03)' } : rec.status === 'expiring_soon' ? { borderColor: 'rgba(217,119,6,0.3)', background: 'rgba(217,119,6,0.03)' } : {}}>
                      <div className="flex items-start justify-between mb-1">
                        <p className="text-xs font-semibold text-text pr-2">{rec.module}</p>
                        <PillBadge value={rec.status} size="xs" />
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-text-dim">
                        <span className="uppercase font-medium">{rec.type}</span>
                        {rec.completionDate && <span>Completed: {format(new Date(rec.completionDate), 'd MMM yyyy')}</span>}
                        {rec.expiryDate && <span>Expires: {format(new Date(rec.expiryDate), 'd MMM yyyy')}</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[11px] text-text-dim">
                        {rec.trainer && <span>Trainer: {rec.trainer}</span>}
                        {rec.score !== undefined && <span className="font-semibold" style={{ color: rec.score >= 80 ? '#16A34A' : '#DC2626' }}>Score: {rec.score}%</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upcoming Renewals */}
            {(selected.allRecords || []).some((r) => r.status === 'expiring_soon' || r.status === 'expired') && (
              <div>
                <p className="text-xs font-semibold text-text-muted mb-2 flex items-center gap-1.5">
                  <AlertTriangle size={12} style={{ color: '#D97706' }} /> Renewals Required
                </p>
                <div className="p-3 rounded-lg" style={{ background: 'rgba(217,119,6,0.06)', border: '1px solid rgba(217,119,6,0.2)' }}>
                  {(selected.allRecords || []).filter((r) => r.status === 'expiring_soon' || r.status === 'expired').map((r) => (
                    <p key={r.id} className="text-xs text-text-muted">
                      <span className="font-medium text-text">{r.module}</span>
                      {r.expiryDate && <> — expires {format(new Date(r.expiryDate), 'd MMM yyyy')}</>}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </SideDrawer>
    </div>
  );
}
